import React, { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/react-hooks';
import type { ApolloError } from 'apollo-client';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import AddIcon from '@atlaskit/icon/core/migration/add';
import Lozenge from '@atlaskit/lozenge';
import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';
import giraClient from '@atlassian/jira-apollo-gira/src/index.tsx';
import { getClient } from '@atlassian/jira-apollo-multiple-clients/src/main.tsx';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import mergeRefs from '@atlassian/jira-merge-refs/src/index.tsx';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { getServiceDeskFeatures } from '@atlassian/jira-project-settings-product-features-jsm-tmp/src/index.tsx';
import { asyncRequestTypeBuilderDrawerEntryPoint } from '@atlassian/jira-servicedesk-request-type-builder/entrypoint.tsx';
import { useIssueTypesNavigationItemsActions } from '@atlassian/jira-spa-apps-project-settings-issue-type/src/controllers/issue-types-navigation-items/index.tsx';
import type { addRequestTypeDropdownQuery } from './__generated_apollo__/addRequestTypeDropdownQuery';
import { FIELDS_LIMIT_QUERY } from './gql.tsx';
import { messages } from './messages.tsx';
import type { AddRequestTypeDropdownProps } from './types.tsx';
import { ConditionalTooltip, transformGiraReponseToHasReachedFieldLimits } from './utils.tsx';

const fireFetchError = (error: ApolloError | Error) => {
	fireErrorAnalytics({
		error,
		meta: {
			id: 'fetchFieldLimitsForAddRequestTypeDropdown',
			packageName: 'jiraNavigationAppsSidebarServicedesk',
			teamName: 'navigation',
		},
	});
};

const entryPointParams = {};
export const AddRequestTypeDropdown = ({ isDisabled, projectId }: AddRequestTypeDropdownProps) => {
	const intl = useIntl();
	const [isRequestTypeBuilderOpen, setIsRequestTypeBuilderOpen] = useState(false);
	const [isCreateBlank, setIsCreateBlank] = useState(false);
	const navActions = useIssueTypesNavigationItemsActions();
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		asyncRequestTypeBuilderDrawerEntryPoint,
		entryPointParams,
	);
	const requestTypeBuilderTrigger = useEntryPointButtonTrigger(entryPointActions);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const client = getClient(giraClient);
	const { data } = useQuery<addRequestTypeDropdownQuery>(FIELDS_LIMIT_QUERY, {
		client,
		variables: { projectId },
		fetchPolicy: 'network-only', // need to ensure we don't get a cached result otherwise we might not get the latest limit info
		onCompleted: (fetchData) => {
			if (fetchData?.projectConfiguration?.__typename === 'JiraProjectConfigurationResult') {
				fireOperationalAnalytics(
					createAnalyticsEvent({}),
					'addRequestTypeDropdowwn.FIELDS_LIMIT_QUERY success',
				);
			} else {
				fireFetchError(new Error('QueryError'));
			}
		},
		onError: fireFetchError,
	});

	const hasReachedFieldLimit = useMemo(
		() => transformGiraReponseToHasReachedFieldLimits(data),
		[data],
	);

	const startTracingRequestTypeBuilder = usePressTracing('jsm-view-tmp-request-type-builder');
	const startTracingBlankRequestTypeBuilder = usePressTracing(
		'jsm-view-tmp-blank-request-type-builder',
	);

	const handleCreateCustom = useCallback(() => {
		if (!isDisabled) {
			setIsCreateBlank(true);
			startTracingBlankRequestTypeBuilder();
			setIsRequestTypeBuilderOpen(true);
		}
	}, [isDisabled, startTracingBlankRequestTypeBuilder]);

	const handleCreateWithTemplate = useCallback(() => {
		if (!isDisabled) {
			setIsCreateBlank(false);
			startTracingRequestTypeBuilder();
			setIsRequestTypeBuilderOpen(true);
		}
	}, [isDisabled, startTracingRequestTypeBuilder]);

	const handleCloseBuilder = useCallback(() => {
		setIsRequestTypeBuilderOpen(false);
	}, []);

	const handleCompleted = useCallback(() => {
		navActions.load(projectId, getServiceDeskFeatures(), intl);
	}, [projectId, intl, navActions]);

	return (
		<>
			<DropdownMenu
				trigger={({ triggerRef, ...triggerProps }) => (
					<MenuItem
						{...triggerProps}
						isDisabled={isDisabled}
						analytics={{ itemId: 'add-issue-type' }}
						ref={mergeRefs(triggerRef, requestTypeBuilderTrigger)}
						iconBefore={<AddIcon spacing="spacious" label="" />}
					>
						{intl.formatMessage(messages.addRequestType)}
					</MenuItem>
				)}
			>
				<DropdownItemGroup>
					<ConditionalTooltip
						shouldShowTooltip={hasReachedFieldLimit}
						tooltipContent={intl.formatMessage(messages.maxFieldsLimitReachedTooltip)}
					>
						<DropdownItem onClick={handleCreateWithTemplate} isDisabled={hasReachedFieldLimit}>
							<FormattedMessage
								{...messages.createFromTemplate}
								values={{
									// This is valid and is clear from the docs https://formatjs.io/docs/react-intl/components/#rich-text-formatting
									// @ts-expect-error Type '(text: string) => JSX.Element' is not assignable to type 'ReactNode'.
									lozenge: (text: string) => <Lozenge appearance="new">{text}</Lozenge>,
								}}
							/>
						</DropdownItem>
					</ConditionalTooltip>
					<DropdownItem onClick={handleCreateCustom}>
						{intl.formatMessage(messages.createCustom)}
					</DropdownItem>
				</DropdownItemGroup>
			</DropdownMenu>
			<JiraEntryPointContainer
				entryPointReferenceSubject={entryPointReferenceSubject}
				id="bundle-async-request-type-builder-entry-point"
				packageName="jira-servicedesk-request-type-builder"
				teamName="jsm-getting-started"
				errorFallback="flag"
				runtimeProps={{
					isOpen: isRequestTypeBuilderOpen,
					onClose: handleCloseBuilder,
					onCompleted: handleCompleted,
					isCreateBlank,
				}}
			/>
		</>
	);
};
